import { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext, API } from '@/App';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { UserPlus, Check, X, Users, Search, ArrowLeft, Send, Inbox, Heart } from 'lucide-react';

const Friends = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('friends');
  const [friends, setFriends] = useState([]);
  const [received, setReceived] = useState([]);
  const [sent, setSent] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { navigate('/auth'); return; }
    fetchAll();
  }, [user]);

  const getToken = () => {
    const token = localStorage.getItem('token');
    return { headers: { Authorization: `Bearer ${token}` } };
  };

  const fetchAll = async () => {
    try {
      const [friendsRes, receivedRes, sentRes] = await Promise.all([
        axios.get(`${API}/friends`, getToken()),
        axios.get(`${API}/friends/requests`, getToken()),
        axios.get(`${API}/friends/sent`, getToken()).catch(() => ({ data: [] }))
      ]);
      setFriends(friendsRes.data || []);
      setReceived(receivedRes.data || []);
      setSent(sentRes.data || []);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;
    setSearching(true);
    try {
      const res = await axios.get(`${API}/users/search?q=${encodeURIComponent(searchQuery)}`, getToken());
      const filtered = (res.data || []).filter(u => u.id !== user.id);
      setSearchResults(filtered);
    } catch (e) {
      toast.error('Search failed');
    } finally {
      setSearching(false);
    }
  };

  const sendRequest = async (toUserId) => {
    try {
      await axios.post(`${API}/friends/request`, { to_user_id: toUserId }, getToken());
      toast.success('Friend request sent!');
      fetchAll();
      setSearchResults(prev => prev.filter(u => u.id !== toUserId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to send request');
    }
  };

  const acceptRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/accept/${requestId}`, {}, getToken());
      toast.success('Friend added!');
      fetchAll();
    } catch (e) {
      toast.error('Failed to accept');
    }
  };

  const rejectRequest = async (requestId) => {
    try {
      await axios.post(`${API}/friends/reject/${requestId}`, {}, getToken());
      toast.success('Request declined');
      fetchAll();
    } catch (e) {
      toast.error('Failed to decline');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-900">
        <div className="text-white">Loading...</div>
      </div>
    );
  }

  const tabs = [
    { id: 'friends', label: 'Friends', icon: Users, count: friends.length },
    { id: 'received', label: 'Received', icon: Inbox, count: received.length },
    { id: 'sent', label: 'Sent', icon: Send, count: sent.length },
    { id: 'add', label: 'Add', icon: UserPlus, count: 0 },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-900/30 to-slate-900">
      {/* Header */}
      <header className="glass sticky top-0 z-50 border-b border-slate-700/50">
        <div className="container mx-auto px-4 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/home')} className="text-slate-400 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <Heart className="w-5 h-5 text-pink-400" />
          <h1 className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>Friends</h1>
        </div>
        {/* Tabs */}
        <div className="container mx-auto px-4 flex gap-1 overflow-x-auto pb-2">
          {tabs.map(tab => (
            <button
              key={tab.id}
              data-testid={`friends-tab-${tab.id}`}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium whitespace-nowrap transition-all ${
                activeTab === tab.id
                  ? 'bg-indigo-600 text-white'
                  : 'text-slate-400 hover:text-white hover:bg-white/5'
              }`}
            >
              <tab.icon className="w-3.5 h-3.5" />
              {tab.label}
              {tab.count > 0 && (
                <Badge className="ml-1 bg-pink-500 text-white text-[10px] px-1.5 py-0">{tab.count}</Badge>
              )}
            </button>
          ))}
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-lg space-y-4">

        {/* FRIENDS LIST */}
        {activeTab === 'friends' && (
          <div data-testid="friends-list-section">
            {friends.length === 0 ? (
              <Card className="glass p-8 border border-slate-700/50 text-center">
                <Users className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No friends yet. Send some requests!</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {friends.map(friend => (
                  <Card
                    key={friend.id}
                    data-testid={`friend-${friend.id}`}
                    className="glass p-3 border border-slate-700/50 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-purple-600 flex items-center justify-center text-white font-bold shrink-0">
                      {friend.username?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm truncate">{friend.username}</p>
                      <p className="text-xs text-slate-500 truncate">{friend.email}</p>
                    </div>
                    <Badge className="bg-green-500/20 text-green-400 border border-green-500/30 text-[10px]">
                      <Check className="w-3 h-3 mr-0.5" /> Friends
                    </Badge>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* RECEIVED REQUESTS */}
        {activeTab === 'received' && (
          <div data-testid="received-requests-section">
            {received.length === 0 ? (
              <Card className="glass p-8 border border-slate-700/50 text-center">
                <Inbox className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No pending friend requests</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {received.map(req => (
                  <Card
                    key={req.id}
                    data-testid={`received-request-${req.id}`}
                    className="glass p-3 border border-pink-500/20 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-pink-500 to-red-600 flex items-center justify-center text-white font-bold shrink-0">
                      {req.from_user?.username?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm truncate">{req.from_user?.username || 'Unknown'}</p>
                      <p className="text-xs text-slate-500">Wants to be friends</p>
                    </div>
                    <div className="flex gap-1.5 shrink-0">
                      <Button
                        data-testid={`accept-request-${req.id}`}
                        size="sm"
                        onClick={() => acceptRequest(req.id)}
                        className="bg-green-600 hover:bg-green-700 h-8 px-3"
                      >
                        <Check className="w-4 h-4" />
                      </Button>
                      <Button
                        data-testid={`reject-request-${req.id}`}
                        size="sm"
                        variant="outline"
                        onClick={() => rejectRequest(req.id)}
                        className="border-red-500/50 text-red-400 h-8 px-3 hover:bg-red-500/10"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* SENT REQUESTS */}
        {activeTab === 'sent' && (
          <div data-testid="sent-requests-section">
            {sent.length === 0 ? (
              <Card className="glass p-8 border border-slate-700/50 text-center">
                <Send className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">No pending sent requests</p>
              </Card>
            ) : (
              <div className="space-y-2">
                {sent.map(req => (
                  <Card
                    key={req.id}
                    data-testid={`sent-request-${req.id}`}
                    className="glass p-3 border border-indigo-500/20 flex items-center gap-3"
                  >
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-blue-600 flex items-center justify-center text-white font-bold shrink-0">
                      {req.to_user?.username?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white text-sm truncate">{req.to_user?.username || 'Unknown'}</p>
                      <p className="text-xs text-slate-500">Request pending</p>
                    </div>
                    <Badge className="bg-amber-500/20 text-amber-400 border border-amber-500/30 text-[10px]">
                      Pending
                    </Badge>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ADD FRIENDS */}
        {activeTab === 'add' && (
          <div data-testid="add-friends-section">
            <div className="flex gap-2 mb-4">
              <Input
                data-testid="friend-search-input"
                placeholder="Search by username or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="bg-slate-800 border-slate-700 text-white"
              />
              <Button
                data-testid="friend-search-btn"
                onClick={handleSearch}
                disabled={searching}
                className="bg-indigo-600 hover:bg-indigo-700 shrink-0"
              >
                <Search className="w-4 h-4" />
              </Button>
            </div>

            {searchResults.length > 0 ? (
              <div className="space-y-2">
                {searchResults.map(u => {
                  const isFriend = friends.some(f => f.id === u.id);
                  const hasSent = sent.some(r => r.to_user_id === u.id);
                  return (
                    <Card
                      key={u.id}
                      className="glass p-3 border border-slate-700/50 flex items-center gap-3"
                    >
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold shrink-0">
                        {u.username?.charAt(0)?.toUpperCase() || '?'}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-white text-sm truncate">{u.username}</p>
                        <p className="text-xs text-slate-500 truncate">{u.email}</p>
                      </div>
                      {isFriend ? (
                        <Badge className="bg-green-500/20 text-green-400 text-[10px]">Already Friends</Badge>
                      ) : hasSent ? (
                        <Badge className="bg-amber-500/20 text-amber-400 text-[10px]">Sent</Badge>
                      ) : (
                        <Button
                          data-testid={`add-friend-${u.id}`}
                          size="sm"
                          onClick={() => sendRequest(u.id)}
                          className="bg-pink-600 hover:bg-pink-700 h-8"
                        >
                          <UserPlus className="w-4 h-4 mr-1" /> Add
                        </Button>
                      )}
                    </Card>
                  );
                })}
              </div>
            ) : (
              <Card className="glass p-8 border border-slate-700/50 text-center">
                <Search className="w-12 h-12 text-slate-600 mx-auto mb-3" />
                <p className="text-slate-400 text-sm">Search for people to add as friends</p>
              </Card>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Friends;
